/**
 * API Client
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R01, AUTH-V1-R02
 *
 * Axios-based API client with authentication interceptors.
 */

import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { UserSession } from '@/types';

// API response types
interface LoginResponse {
  user: UserSession;
  token: string;
}

interface ApiErrorResponse {
  message?: string;
  detail?: string;
  code?: string;
  retryAfter?: number;
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: Error) => Promise.reject(error)
);

// Response interceptor - handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // AUTH-V1-R02 / Bug 4366: suppress wipe + redirect during the SAML
      // token-exchange handoff. App-level contexts (FeatureFlagContext,
      // branding) fetch protected /v1/config/* endpoints on mount; on
      // /sign-in-token/<jwt> they race the in-flight POST to
      // /v1/auth/saml/exchange. Allowing the 401 to clear localStorage and
      // redirect to /login would destroy the session token before the
      // exchange persists it, breaking SSO sign-in entirely.
      // SignInTokenPage retains its own error handling for genuine
      // exchange failures (token expiry, invalid signature).
      const isSignInTokenHandoff =
        window.location.pathname.startsWith('/sign-in-token/');

      if (!isSignInTokenHandoff) {
        // Clear stored auth on unauthorized
        localStorage.removeItem('auth-token');
        localStorage.removeItem('galen-auth-storage');

        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          // AUTH-V1-R09: Detect session inactivity timeout for user messaging
          const errorData = error.response?.data as { error_code?: string } | undefined;
          const isInactivityTimeout =
            errorData?.error_code === 'SESSION_INACTIVE_TIMEOUT';
          window.location.href = isInactivityTimeout
            ? '/login?reason=inactivity'
            : '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API functions
export const authApi = {
  login: async (username: string, password: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', {
      username,
      password,
    });

    // Store token
    if (response.data.token) {
      localStorage.setItem('auth-token', response.data.token);
    }

    return response.data;
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('auth-token');
    }
  },

  refreshToken: async (): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/refresh');
    if (response.data.token) {
      localStorage.setItem('auth-token', response.data.token);
    }
    return response.data;
  },
};

// Error message utility
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;

    // Handle rate limiting
    if (status === 429) {
      const retryAfter = data?.retryAfter || 60;
      return `Too many attempts. Please try again in ${retryAfter} seconds.`;
    }

    // Handle account locked
    if (status === 423 || data?.code === 'ACCOUNT_LOCKED') {
      return 'Your account has been locked. Please contact support for assistance.';
    }

    // Handle invalid credentials
    if (status === 401) {
      return 'Invalid username or password. Please try again.';
    }

    // Handle server errors
    if (status && status >= 500) {
      return 'A server error occurred. Please try again later.';
    }

    // Handle network errors
    if (axiosError.code === 'ERR_NETWORK' || !axiosError.response) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    // Use server-provided message if available
    if (data?.message) {
      return data.message;
    }

    // Bug #3900: Handle FastAPI error format (uses 'detail' field)
    if (data?.detail && typeof data.detail === 'string') {
      return data.detail;
    }
  }

  // Fallback for non-Axios errors
  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
}

export default api;
